import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { COLOR, FONT, RADIUS, Z, SPRING } from "../lib/tokens";

/**
 * ModalSheet — shared bottom-sheet modal primitive.
 *
 * Before this existed, every modal in the app (ItemCard, LinkIngredient,
 * AddItemModal, IngredientDetailSheet, ConvertStateModal, CookComplete
 * phase shells, delete-confirm, ReceiptView, etc.) hand-rolled its own
 * fixed-position backdrop + inner sheet container + close affordances.
 * That meant cross-cutting behavior (swipe-down-to-dismiss, Escape key,
 * drag handle, z-index management) was implemented inconsistently:
 * swipe-down lived only on ItemCard, Escape handling was ad-hoc,
 * z-indexes drifted across components.
 *
 * This component owns that shell. Every new modal should use it;
 * existing modals can migrate opportunistically as they're touched.
 *
 * Features (all opt-in via props with sensible defaults):
 *   - Backdrop with fade-with-drag when swipeable
 *   - Centered drag handle (can be hidden via showHandle=false)
 *   - Top-right ✕ close button (hidden with showClose=false)
 *   - Escape key to close
 *   - Click-backdrop to close
 *   - Swipe-down-to-dismiss (enabled by default; set swipeable=false to
 *     disable for modals where vertical scrolling fights the gesture,
 *     e.g. pickers with lots of content)
 *   - z-index from the token scale (default Z.card = 320)
 *   - Motion-driven enter animation: spring-slide up from bottom,
 *     backdrop fade-in, via framer-motion using the SPRING.sheet preset.
 *     The existing drag-to-dismiss is preserved; motion and drag share
 *     the `y` value so the spring controller honors live drag position.
 *
 * Props:
 *   onClose()         - called when any dismissal happens. Caller owns
 *                       the open/close state; this component never
 *                       owns it internally.
 *   children          - the sheet's body
 *   zIndex            - stacking layer. Default Z.card; pickers should
 *                       pass Z.picker, destructive confirms Z.confirm
 *   swipeable         - enable swipe-down gesture (default true)
 *   showHandle        - show the centered drag handle (default true)
 *   showClose         - show the top-right ✕ (default true)
 *   maxHeight         - CSS max-height for the inner sheet. Default 92vh
 *   closeOnBackdrop   - tap backdrop to close (default true)
 *   label             - optional kicker label shown above the children,
 *                       styled as an uppercase mono tag. Convenient for
 *                       modals that would otherwise reimplement this
 *                       same header pattern by hand.
 *   scrollRef         - optional ref to the inner scrollable container.
 *                       The swipe gesture only activates when this
 *                       container is scrolled to the top; pass your
 *                       own ref if the modal's content is itself
 *                       scrollable and you want the gesture aware of
 *                       that scroll state. Omit and a sensible default
 *                       is used (the ModalSheet's own scroll element).
 */
export default function ModalSheet({
  onClose,
  children,
  zIndex = Z.card,
  swipeable = true,
  showHandle = true,
  showClose = true,
  maxHeight = "92vh",
  // height — when set, locks the sheet to a fixed height so the body
  // doesn't shrink to fit short content. Used by AddItemSheet to keep
  // the sheet shape stable across pipeline transitions. Pass a vh /
  // dvh / px value; falls through to ModalSheet's normal grow-to-fit
  // behavior when omitted.
  height,
  closeOnBackdrop = true,
  label,
  scrollRef: scrollRefFromProps,
}) {
  // Escape-to-close. Every modal wants this; putting it here means
  // consumers never forget to wire it and it never diverges.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Visible-height tracking for vh-unit resolution. ONLY responsibility
  // here: keep `visibleH` synced to visualViewport.height so the sheet's
  // `maxHeight: 92vh` shrinks to fit the actually-visible area when the
  // iOS keyboard is open. Keyboard offset / lift is handled by the
  // global `.kb-lift` class wrapping the motion sheet (uses
  // var(--kb-offset) set by useKeyboardOffset); we do NOT track it
  // locally anymore. Per 2026-05-23 audit, commit 5b/5.
  const [visibleH, setVisibleH] = useState(null);
  useEffect(() => {
    const vv = typeof window !== "undefined" && window.visualViewport;
    if (!vv) return;
    const update = () => setVisibleH(Math.round(vv.height));
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);


  // Swipe-down state. Dragged downward past DISMISS_THRESHOLD fires
  // onClose with an animated slide-out; anything less snaps back.
  //
  //   dragY        - current vertical offset the sheet is translated by
  //   dragStartRef - ref holding touch-start Y + whether scroll was at
  //                  the top when drag began. Gesture only activates
  //                  at scrollTop=0 so scrolling long content never
  //                  accidentally triggers dismiss.
  const DISMISS_THRESHOLD = 100;
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef(null);
  const internalScrollRef = useRef(null);
  const scrollRef = scrollRefFromProps || internalScrollRef;

  // stopPropagation on every touch handler — nested ModalSheets
  // (e.g. TypePicker opened from inside AddItemModal) otherwise
  // let touches bubble to the parent sheet's handlers, so a
  // swipe-down on the inner picker drags the outer AddItemModal
  // too and both dismiss at once.
  const onTouchStart = (e) => {
    if (!swipeable) return;
    e.stopPropagation();
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop > 0) return;  // let the user scroll up first
    dragStartRef.current = { y: e.touches[0].clientY };
    setDragging(true);
  };
  const onTouchMove = (e) => {
    if (!swipeable) return;
    e.stopPropagation();
    if (!dragStartRef.current) return;
    const diff = e.touches[0].clientY - dragStartRef.current.y;
    if (diff <= 0) { setDragY(0); return; }  // upward drags don't count
    setDragY(diff);
  };
  const onTouchEnd = (e) => {
    if (!swipeable) return;
    e?.stopPropagation?.();
    if (!dragStartRef.current) return;
    const finalY = dragY;
    dragStartRef.current = null;
    setDragging(false);
    if (finalY >= DISMISS_THRESHOLD) {
      // Slide off-screen then dismiss. The setTimeout matches the
      // transition duration so the onClose lands right as the sheet
      // is visually gone.
      setDragY(window.innerHeight);
      setTimeout(() => onClose?.(), 180);
    } else {
      setDragY(0);
    }
  };

  // Backdrop opacity drops with drag distance so dismiss feels
  // physical. While the user is actively dragging, we disable the
  // spring so the backdrop tracks the finger 1:1.
  const backdropOpacity = Math.max(0, 1 - dragY / 400);

  // Resolve the effective maxHeight. If the caller passed a vh-based
  // string (default "92vh"), translate the percentage against the
  // visualViewport's height when we have it so the sheet shrinks as
  // the keyboard opens. Any non-vh value (px, dvh, etc.) passes
  // through untouched.
  // Match vh / lvh / dvh / svh — all of them represent "X% of some
  // viewport height," and we want the sheet to follow visualViewport
  // (i.e. shrink when the iOS keyboard opens) regardless of which
  // unit the caller picked. Without lvh/dvh in the alternation, the
  // AddItemSheet's `86lvh` lock skipped this resolver entirely and
  // the sheet stayed at full layout-viewport height while ALSO
  // getting translated up by keyboardOffset — which shoved its top
  // off the top of the screen on iOS.
  const VH_LIKE = /^([\d.]+)(vh|lvh|dvh|svh)$/;
  const resolvedMaxHeight = (() => {
    if (!visibleH) return maxHeight;
    const m = VH_LIKE.exec(String(maxHeight).trim());
    if (!m) return maxHeight;
    const pct = parseFloat(m[1]) / 100;
    return `${Math.round(visibleH * pct)}px`;
  })();
  const resolvedHeight = (() => {
    if (!height) return undefined;
    if (!visibleH) return height;
    const m = VH_LIKE.exec(String(height).trim());
    if (!m) return height;
    const pct = parseFloat(m[1]) / 100;
    return `${Math.round(visibleH * pct)}px`;
  })();

  // (Local focus-in scroll-into-view removed — the single root
  // focusin listener in useKeyboardOffset now handles centering for
  // every focused input app-wide. Per 2026-05-23 audit, commit 5b/5.)
  const assignSheetRef = (node) => {
    if (typeof scrollRef === "function") scrollRef(node);
    else if (scrollRef) scrollRef.current = node;
  };

  // Render through a document.body portal — without this, any ancestor
  // with a transform / filter / will-change / contain creates a
  // stacking context that traps our position:fixed inside it. A
  // ResolveCard with a `position: relative` parent (or Capacitor's
  // outer wrapper applying willChange) was causing the wizard's
  // backdrop to NOT cover the viewport. Portal escapes that.
  if (typeof document === "undefined") return null;
  const tree = (
    <motion.div
      onClick={closeOnBackdrop ? onClose : undefined}
      initial={{ opacity: 0 }}
      animate={{ opacity: backdropOpacity }}
      transition={dragging ? { duration: 0 } : { duration: 0.2, ease: "easeOut" }}
      style={{
        // Backdrop covers the FULL viewport — no maxWidth here, or
        // the dim leaves black gaps on either side on wider phones /
        // tablets / desktop. The 480px reading-width cap belongs on
        // the inner card so the SHEET stays narrow but the dim
        // extends edge-to-edge. justifyContent:"center" keeps the
        // bounded card centered horizontally.
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.87)",
        zIndex,
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <motion.div
        ref={assignSheetRef}
        onClick={e => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        initial={{ y: "100%" }}
        // y is only the drag offset now (positive = drag down toward
        // dismiss). Keyboard lift is owned by the parent backdrop —
        // its inline `position: fixed` + `align-items: flex-end`
        // pair matches the layer-1 attribute selector in
        // public/index.html which auto-applies
        // `padding-bottom: var(--kb-offset)`. That padding pushes
        // this flex-end-anchored sheet UP by exactly the keyboard
        // height. Per 2026-05-23 audit, commit 5b/5 — the local
        // keyboardOffset state + animate subtraction was a
        // duplicate of what layer-1 already did.
        animate={{ y: dragY }}
        transition={dragging ? { duration: 0 } : SPRING.sheet}
        style={{
          // Card is bounded for desktop readability but stretches
          // edge-to-edge on phones (≤480px viewport).
          width: "100%", maxWidth: 480,
          background: COLOR.ground,
          borderRadius: `${RADIUS.sheet}px ${RADIUS.sheet}px 0 0`,
          padding: "18px 22px 36px",
          maxHeight: resolvedMaxHeight,
          ...(resolvedHeight ? { height: resolvedHeight } : null),
          overflowY: "auto",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          // iOS overscroll-bounce chains to the whole page otherwise,
          // which fights the gesture. Contain it here.
          overscrollBehaviorY: "contain",
          willChange: "transform",
        }}
      >
        {/* Close rides in a zero-height STICKY wrapper so it pins to
            the top of the scrollport instead of scrolling away with
            the content. The old `position: absolute` anchored it to
            the scroll container's content box — on any sheet taller
            than the viewport (EatQuickSheet with package chips, long
            pickers) one scroll hid the only escape hatch and the
            sheet read as inescapable. Sticky + height 0 keeps layout
            identical at rest and the ✕ reachable at every scroll
            position. */}
        {showClose && (
          <div style={{
            position: "sticky", top: 0, zIndex: 2,
            height: 0, display: "flex", justifyContent: "flex-end",
            flexShrink: 0,
          }}>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                width: 36, height: 36, marginTop: -6, marginRight: -8,
                background: COLOR.deep, border: `1px solid ${COLOR.border}`,
                color: COLOR.dim, borderRadius: 18,
                fontFamily: FONT.mono, fontSize: 14,
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
        )}
        {showHandle && (
          <div style={{
            width: 44, height: 4, background: COLOR.border,
            borderRadius: 2, margin: "0 auto 14px", flexShrink: 0,
          }} />
        )}
        {label && (
          <div style={{
            fontFamily: FONT.mono, fontSize: 10,
            color: COLOR.gold, letterSpacing: "0.12em",
            paddingTop: 4, marginBottom: 6,
          }}>
            {label}
          </div>
        )}
        {children}
      </motion.div>
    </motion.div>
  );
  return createPortal(tree, document.body);
}
